import type { FC } from 'react';
import React, { useCallback, useContext, useState, useMemo, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { mergeRefs } from 'use-callback-ref';

import type { PopperChildrenProps } from '@atlaskit/popper';
import { Manager, Reference } from '@atlaskit/popper';
import { IconButton } from '@atlaskit/button/new';
import Tooltip from '@atlaskit/tooltip';
import Portal from '@atlaskit/portal';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { SpotlightPulse } from '@atlaskit/onboarding';
import AiGenerativeAudioBriefingIcon from '@atlaskit/icon-lab/core/ai-generative-audio-briefing';

import type { AIEventsInstrumentationConfig } from '@atlassian/ai-analytics';
import {
	AIEventsInstrumentationProvider,
	useAIEventsInstrumentation,
} from '@atlassian/ai-analytics';

import type { ActiveAudioContent } from '@confluence/audio/entry-points/activeAudioContent';
import { AI_AUDIO_BRIEFING_INTERACTION_METRIC } from '@confluence/audio/entry-points/perf.config';
import { getSharedAttributes } from '@confluence/audio/entry-points/getSharedAttributes';
import { useAudioBriefingChangeboarding } from '@confluence/audio/entry-points/useAudioBriefingChangeboarding';
import { AudioPopupContent } from '@confluence/audio/entry-points/AudioPopupContent';
import { AudioBriefingPopupContent } from '@confluence/audio/entry-points/AudioBriefingPopupContent';
import { AudioFeature } from '@confluence/audio/entry-points/audioFeatureType';
import { useAudioBriefing } from '@confluence/audio/entry-points/useAudioBriefing';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import {
	AI_AUDIO_BRIEFING,
	AI_AUDIO_NARRATION,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { expVal } from '@confluence/feature-experiments';
import { useAIRovoButtonEnablement } from '@confluence/ai-floating-context-menu/entry-points/useAIRovoButtonEnablement';
import { useIsObjectSidebarShown } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';

import { useOnDetectOutsideClick } from '../hooks/useOnDetectOutsideClick';

const i18n = defineMessages({
	listen: {
		id: 'object-sidebar-components.audio.button.tooltip',
		defaultMessage: 'Listen',
		description: 'Message in tooltip which tells user that this button is tied to audio features',
	},
	browserNotSupported: {
		id: 'object-sidebar-components.audio.button.browserNotSupported',
		defaultMessage: 'Your browser doesn’t support this feature',
		description:
			'Message in tooltip which tells user that their browser does not support Audio Briefing feature',
	},
});

type AudioButtonProps = {
	contentId: string;
	contentType?: string | null;
	spaceKey?: string;
	contentTitle?: string | null;
	isDisabled?: boolean;
};

const AudioButtonComponent: FC<AudioButtonProps> = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
	isDisabled = false,
}) => {
	const { formatMessage, locale } = useIntl();

	const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
	const [isPopupMinimized, setIsPopupMinimized] = useState<boolean>(false);

	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const popupRef = useRef<HTMLDivElement | null>(null);

	const renderServerPlaceholder = useRenderServerPlaceholder();

	const audioSupportedByBrowser =
		!renderServerPlaceholder &&
		typeof MediaSource !== 'undefined' &&
		MediaSource.isTypeSupported('audio/mpeg');

	const isObjectSidebarShown = useIsObjectSidebarShown();

	const [
		{
			activeContent: prevActiveContent,
			activeFeature,
			hasFetched,
			streamEnded,
			error,
			isLoading,
			customization,
			additionalResources,
			lastGeneratedBriefingConfig,
			playbackSpeed,
			hasEndBeenReached,
			isPlaying,
		},
		{ initialize, fetchBriefing },
	] = useAudioBriefing();

	const { cloudId, activationId } = useSessionData();

	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { trackAIInteractionDismiss, trackAIResultAction, trackAIInteractionInit } =
		useAIEventsInstrumentation();

	const { Popper } = useChoreographerAssets();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const pageMode = useGetPageMode();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'audioButton',
				attributes: {
					source: 'contentWrapper',
					locale,
				},
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createAnalyticsEvent, contentId]);

	const sendAnalyticsUIEvent = useCallback(
		(action: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'audioBriefingPopup',
					source: 'contentWrapper',
					attributes: {
						action,
						contentType,
						pageMode,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, contentType, pageMode],
	);

	const activeContent: ActiveAudioContent = useMemo(
		() => ({
			id: contentId,
			// Should we just fail if there is no contentType?
			contentType: contentType ?? 'page',
			spaceKey,
			contentTitle,
		}),
		[contentId, contentType, spaceKey, contentTitle],
	);

	const handleOpen = useCallback(() => {
		initialize({
			activeContent,
			cloudId,
			activationId,
			locale,
		});
		setIsPopupOpen(true);
	}, [initialize, activeContent, cloudId, activationId, locale]);

	const handleOpenAndFetchBriefing = useCallback(() => {
		handleOpen();

		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});
		AI_AUDIO_BRIEFING_INTERACTION_METRIC.start();
		fetchBriefing();

		trackAIInteractionInit({
			attributes: getSharedAttributes({
				customization,
				additionalResources,
				playbackSpeed,
				hasFetched,
				activeFeature: AudioFeature.BRIEFING,
			}),
		});
	}, [
		handleOpen,
		experienceTracker,
		activeContent,
		fetchBriefing,
		trackAIInteractionInit,
		customization,
		additionalResources,
		playbackSpeed,
		hasFetched,
	]);

	const { showChangeboarding, handleCloseChangeboarding } = useAudioBriefingChangeboarding({
		contentId,
		onShow: handleOpen,
		skip: !audioSupportedByBrowser,
	});

	useOnDetectOutsideClick({
		qualifiers: [isPopupOpen, isPlaying],
		refs: [popupRef, triggerRef],
		onOutsideClick: () => {
			setIsPopupMinimized(true);
		},
	});

	const handleClose = () => {
		setIsPopupOpen(false);
	};

	const handleCloseWithAnalytics = (additionalAttributes?: Record<string, any>) => {
		if (hasFetched && !error) {
			const feature =
				activeFeature === AudioFeature.BRIEFING ? AI_AUDIO_BRIEFING : AI_AUDIO_NARRATION;
			if (!streamEnded) {
				experienceTracker.abort({
					name: feature,
					reason: 'briefing popup was closed',
				});
			}

			if (isLoading) {
				trackAIInteractionDismiss({
					attributes: {
						...getSharedAttributes({
							customization,
							additionalResources,
							playbackSpeed,
							hasFetched,
							activeFeature,
						}),
					},
				});
			} else {
				trackAIResultAction('audioClosed', {
					attributes: {
						...getSharedAttributes({
							customization: lastGeneratedBriefingConfig?.customization,
							additionalResources: lastGeneratedBriefingConfig?.additionalResources,
							playbackSpeed,
							hasFetched,
							activeFeature,
						}),
						...additionalAttributes,
						hasEndBeenReached,
					},
				});
			}
		}
		AI_AUDIO_BRIEFING_INTERACTION_METRIC.stop({ customData: { dismissed: true } });
		handleClose();
	};

	const handleAudioButtonClick = () => {
		if (isPopupOpen) {
			if (showChangeboarding) {
				handleCloseChangeboarding();
				sendAnalyticsUIEvent('closeChangeboarding');
				return;
			}
			if (isPopupMinimized) {
				setIsPopupMinimized(false);
				sendAnalyticsUIEvent('expandPopup');
				return;
			}
			handleCloseWithAnalytics();
			sendAnalyticsUIEvent('closePopup');
		} else {
			if (!showChangeboarding && expVal('confluence_audio_briefing_autoplay', 'isEnabled', false)) {
				handleOpenAndFetchBriefing();
			} else {
				handleOpen();
			}
			sendAnalyticsUIEvent('openPopup');
		}
	};

	// Close popper if the contentId changes meaning the user has navigated to different content
	if (isPopupOpen && prevActiveContent?.id !== contentId) {
		handleCloseWithAnalytics();
	}

	// Close popper if the object sidebar is shown since the button will now be hidden
	if (isPopupOpen && isObjectSidebarShown) {
		handleCloseWithAnalytics();
	}

	const tooltipContent = audioSupportedByBrowser
		? formatMessage(i18n.listen)
		: formatMessage(i18n.browserNotSupported);

	// Refactored to avoid React.Fragment issues with conditional rendering of the SpotlightPulse component
	const renderIconButton = (ref: React.Ref<HTMLButtonElement>) => (
		<IconButton
			label={formatMessage(i18n.listen)}
			appearance="subtle"
			icon={AiGenerativeAudioBriefingIcon}
			shape={isRovoButtonEnabled ? 'default' : 'circle'}
			onClick={handleAudioButtonClick}
			isSelected={isPopupOpen && !showChangeboarding}
			testId="audio-button"
			ref={ref}
			isDisabled={
				(isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)) ||
				!audioSupportedByBrowser
			}
		/>
	);

	const renderPopperContent = () => {
		if (expVal('confluence_audio_narrations', 'isEnabled', false)) {
			return (
				<AudioPopupContent
					closePopup={handleCloseWithAnalytics}
					isPopupMinimized={isPopupMinimized}
					setIsPopupMinimized={setIsPopupMinimized}
					showChangeboarding={showChangeboarding}
					handleCloseChangeboarding={handleCloseChangeboarding}
				/>
			);
		}

		return (
			<AudioBriefingPopupContent
				closePopup={handleCloseWithAnalytics}
				isPopupMinimized={isPopupMinimized}
				setIsPopupMinimized={setIsPopupMinimized}
				showChangeboarding={showChangeboarding}
				handleCloseChangeboarding={handleCloseChangeboarding}
			/>
		);
	};

	return (
		<Manager>
			<Reference>
				{({ ref }) => (
					<Tooltip content={tooltipContent} position="left" hideTooltipOnClick>
						{showChangeboarding ? (
							<SpotlightPulse radius={3}>
								{renderIconButton(mergeRefs([ref, triggerRef]))}
							</SpotlightPulse>
						) : (
							<React.Fragment>{renderIconButton(mergeRefs([ref, triggerRef]))}</React.Fragment>
						)}
					</Tooltip>
				)}
			</Reference>
			{isPopupOpen && (
				<Popper
					placement="left-end"
					offset={[0, 16]}
					strategy="absolute"
					messageType={showChangeboarding ? 'engagement' : 'transactional'}
					messageId="confluence-ai-audio-briefing"
				>
					{({ ref, style }: PopperChildrenProps) => (
						<Portal>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
							<div ref={mergeRefs([ref, popupRef])} style={style}>
								{renderPopperContent()}
							</div>
						</Portal>
					)}
				</Popper>
			)}
		</Manager>
	);
};

export const AudioButton = (passThroughProps: AudioButtonProps): JSX.Element => {
	const { contentId } = passThroughProps;

	const aiAnalyticsConfig: AIEventsInstrumentationConfig = {
		product: 'confluence',
		subproduct: 'confluence',
		source: 'briefingPopup',
		aiFeatureName: 'aiAudioBriefing',
		proactiveGeneratedAI: 0,
		userGeneratedAI: 1,
		additionalAttributes: {
			contentId,
		},
	};

	return (
		<ErrorBoundary attribution={Attribution.SMARTS}>
			<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
				<AudioButtonComponent {...passThroughProps} />
			</AIEventsInstrumentationProvider>
		</ErrorBoundary>
	);
};
